/**
 * Resolve viewer invites (Controller → UDV clínico).
 * Contrato: docs/CONTRATO-UDV-PERSONAS.md
 */
import { getControllerUrl } from './controllerAuth'
import { parsePlanQueryParam } from '../plan/parsePlanQuery'
import { parseThemeQueryParam } from '../plan/chromeTheme'
import { ENTITLEMENT_SOURCES, UDV_PLANS } from '../plan/constants'
import { saveEntitlements } from '../plan/entitlements'
import { setStoredPersona, UDV_PERSONAS } from '../plan/persona'

/**
 * @param {*} raw
 */
export function normalizeInviteResponse(raw) {
  if (!raw || typeof raw !== 'object') return null
  const status = String(raw.status || '')
    .trim()
    .toLowerCase()
  const inviteId = String(raw.invite_id || raw.inviteId || '').trim()
  if (!inviteId) return null
  const plan =
    parsePlanQueryParam(raw.plan) ||
    (status === 'pending' ? UDV_PLANS.LITE : null)
  return {
    inviteId,
    edgeId: raw.edge_id != null ? String(raw.edge_id) : null,
    clinicName: raw.clinic_name != null ? String(raw.clinic_name) : null,
    emailHint: raw.email_hint != null ? String(raw.email_hint) : null,
    plan,
    shell: raw.shell != null ? String(raw.shell) : null,
    theme: parseThemeQueryParam(raw.theme),
    pacsId: raw.pacs_id != null ? String(raw.pacs_id) : null,
    expiresAt: raw.expires_at != null ? String(raw.expires_at) : null,
    status: status || 'pending',
  }
}

/**
 * @param {string} inviteId
 * @param {{ fetchImpl?: typeof fetch, baseUrl?: string }} [opts]
 */
export async function resolveViewerInvite(inviteId, opts = {}) {
  const id = String(inviteId || '').trim()
  if (!id) {
    return { ok: false, reason: 'empty' }
  }
  const fetchImpl = opts.fetchImpl || fetch
  const base = (opts.baseUrl || getControllerUrl()).replace(/\/+$/, '')
  const url = `${base}/api/v1/invites/${encodeURIComponent(id)}`
  let response
  try {
    response = await fetchImpl(url, {
      method: 'GET',
      headers: { Accept: 'application/json' },
    })
  } catch (e) {
    return { ok: false, reason: 'network', error: e }
  }
  if (response.status === 404 || response.status === 410) {
    return { ok: false, reason: 'invalid', status: response.status }
  }
  if (!response.ok) {
    return { ok: false, reason: 'http', status: response.status }
  }
  let data
  try {
    data = await response.json()
  } catch (e) {
    return { ok: false, reason: 'invalid_json' }
  }
  const invite = normalizeInviteResponse(data)
  if (!invite) {
    return { ok: false, reason: 'invalid_json' }
  }
  if (invite.status === 'expired' || invite.status === 'revoked') {
    return { ok: false, reason: 'invalid', invite }
  }
  if (invite.status === 'consumed') {
    return { ok: false, reason: 'consumed', invite }
  }
  if (invite.status !== 'pending') {
    return { ok: false, reason: 'invalid', invite }
  }
  return { ok: true, invite }
}

/**
 * Persiste draft entitlements desde invite pending (antes de login).
 * @param {*} invite — normalizeInviteResponse
 */
export function applyInviteDraftEntitlements(invite) {
  if (!invite || !invite.plan) return null
  setStoredPersona(UDV_PERSONAS.CLINICAL)
  return saveEntitlements({
    plan: invite.plan,
    shell: invite.shell,
    theme: invite.theme,
    pacsId: invite.pacsId,
    edgeId: invite.edgeId,
    clinicName: invite.clinicName,
    source: ENTITLEMENT_SOURCES.INVITE,
  })
}
